import PropTypes from 'prop-types';
import { useState } from 'react';
import constants from '../../config/constants';
import { noop } from '../../utils/noop';
import ColorSwatch from '../ui/ColorSwatch/ColorSwatch';
import { shouldShowExpandable } from './ProductColorSwatches.helpers';
import { ProductColorSwatchesStyles } from './ProductColorSwatches.style';

const RegularColorSwatches = ({
  productId,
  colorVariants,
  type,
  handleColorSwatch,
  selectedColorIndex,
  size,
  appCtx,
  colorMetrics,
  variantSwatchType,
  inDeliveryOptions,
}) => {
  return (
    <div className={`color-swatch-container color-swatch-${type} mkp-color-swatch`}>
      <ColorSwatch
        colorVariants={colorVariants}
        handleColorSwatch={handleColorSwatch}
        selectedColorIndex={selectedColorIndex}
        productId={productId}
        key={productId}
        size={size}
        page={constants.PAGES.PDP}
        appCtx={appCtx}
        colorMetrics={colorMetrics}
        variantSwatchType={variantSwatchType}
        inDeliveryOptions={inDeliveryOptions}
      />
      <style jsx>{ProductColorSwatchesStyles}</style>
    </div>
  );
};

RegularColorSwatches.defaultProps = {
  handleColorSwatch: noop,
  selectedColorIndex: 0,
  variantSwatchType: undefined,
  inDeliveryOptions: false,
};

RegularColorSwatches.propTypes = {
  colorVariants: PropTypes.array.isRequired,
  productId: PropTypes.string.isRequired,
  handleColorSwatch: PropTypes.func,
  type: PropTypes.string.isRequired,
  selectedColorIndex: PropTypes.number,
  size: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  colorMetrics: PropTypes.object.isRequired,
  variantSwatchType: PropTypes.string,
  inDeliveryOptions: PropTypes.bool,
};

const ExpandableColorSwatches = ({
  productId,
  colorVariants,
  type,
  handleColorSwatch,
  selectedColorIndex,
  size,
  appCtx,
  colorMetrics,
  variantSwatchType,
}) => {
  const [expandState, changeExpand] = useState('collapse');
  const toggleExpand = () => {
    const updatedState = expandState === 'collapse' ? 'expand' : 'collapse';
    changeExpand(updatedState);
  };
  const collapseClass =
    variantSwatchType === 'IMAGE' ? `color-swatch-${type}-collapse-image` : `color-swatch-${type}-collapse`;

  return (
    <div className="expandable-wrapper">
      <div
        className={`color-swatch-container
            color-swatch-${type}
            ${expandState === 'collapse' ? collapseClass : ''}
          `}
      >
        <ColorSwatch
          colorVariants={colorVariants}
          handleColorSwatch={handleColorSwatch}
          selectedColorIndex={selectedColorIndex}
          productId={productId}
          key={productId}
          size={size}
          page={constants.PAGES.PDP}
          appCtx={appCtx}
          colorMetrics={colorMetrics}
          variantSwatchType={variantSwatchType}
        />
      </div>
      <button type="button" id="testId-expand-toggle" className="expand-swatch" onClick={toggleExpand}>
        {expandState === 'collapse' && (
          <>
            <span className="plus-icon" />
            <span className="collapse-text">Mostrar más colores</span>
          </>
        )}
      </button>
      <style jsx>{ProductColorSwatchesStyles}</style>
    </div>
  );
};

ExpandableColorSwatches.propTypes = {
  colorVariants: PropTypes.array.isRequired,
  productId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  handleColorSwatch: PropTypes.func.isRequired,
  selectedColorIndex: PropTypes.number.isRequired,
  size: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  colorMetrics: PropTypes.object.isRequired,
  variantSwatchType: PropTypes.string.isRequired,
};

const ProductColorSwatches = ({
  colorVariants,
  productId,
  type,
  selectedColorSwatchIndex,
  colorSwatchHandler,
  size,
  appCtx,
  colorMetrics,
  variantSwatchType,
  inDeliveryOptions,
}) => {
  const { deviceType } = appCtx;

  // Using helper to expandable
  const isRegularColorSwatch = !shouldShowExpandable(variantSwatchType, colorVariants, deviceType);

  return isRegularColorSwatch ? (
    <RegularColorSwatches
      productId={productId}
      colorVariants={colorVariants}
      type={type}
      handleColorSwatch={colorSwatchHandler}
      selectedColorIndex={selectedColorSwatchIndex}
      size={size}
      appCtx={appCtx}
      colorMetrics={colorMetrics}
      variantSwatchType={variantSwatchType}
      inDeliveryOptions={inDeliveryOptions}
    />
  ) : (
    <ExpandableColorSwatches
      productId={productId}
      colorVariants={colorVariants}
      type={type}
      handleColorSwatch={colorSwatchHandler}
      selectedColorIndex={selectedColorSwatchIndex}
      size={size}
      appCtx={appCtx}
      colorMetrics={colorMetrics}
      variantSwatchType={variantSwatchType}
    />
  );
};

ProductColorSwatches.defaultProps = {
  type: constants.PRODUCT_CONSTANTS.HARDLINE,
  selectedColorSwatchIndex: 0,
  colorSwatchHandler: noop,
  size: 'medium',
  variantSwatchType: undefined,
  inDeliveryOptions: false,
};

ProductColorSwatches.propTypes = {
  colorVariants: PropTypes.array.isRequired,
  productId: PropTypes.string.isRequired,
  type: PropTypes.string,
  selectedColorSwatchIndex: PropTypes.number,
  colorSwatchHandler: PropTypes.func,
  size: PropTypes.oneOf(['medium', 'large']),
  appCtx: PropTypes.object.isRequired,
  colorMetrics: PropTypes.object.isRequired,
  variantSwatchType: PropTypes.string,
  inDeliveryOptions: PropTypes.bool,
};

export default ProductColorSwatches;
